import React, {
  useCallback, useState, useMemo, useEffect,
} from 'react';
import { AxiosResponse } from 'axios';
import {
  fetchRegionFeatured,
  fetchRegionAccommodations, fetchRegionMeta, fetchRegionRelated,
} from 'api/region/static';
import { IRegionShort } from 'api/region/types';
import { CurrentLink, CurrentTab } from 'components/region/RegionHeaderNav/constants';
import { HeaderNavCurLink } from 'components/common/HeaderNav';
import { useRegionLayoutBubble } from 'page-components/region/_context';
import { IRegionLayout } from 'page-components/region/_layout';
import { RouteAndAnalyticsProps } from 'page-components/pathTypes';
import { getRegionSlug } from 'util/memcache';
import { ITALodgingReq } from 'components/common/PlanYourTrip/types';
import { IAccommodationPage } from 'api/tripadvisor/types';
import PlanYourTrip from 'components/common/PlanYourTrip/RegionWidget';
import AccommodationsList from 'components/region/AccommodationsList';
import { Hero400Error } from 'components/400';
import { assertShouldBeNot404, wrapServerSideProps, wrapStaticProps } from 'util/wrapProps';
import { hasWeSkiIntegration, weSkiDestinationId } from 'components/common/PlanYourTrip/WeSki/resolver';
import WeSkiPage from 'components/common/PlanYourTrip/WeSki/WeSkiPage';
import { useRouter } from 'next/router';
import { getLocale } from 'i18n/locale';
import { isRegionWithoutWeSki } from 'util/isRegionWithoutWeSki';
import { Routes } from 'routes';

export type RegionLodgingPageProps = {
  accommodations: IAccommodationPage | null,
  numResorts: number,
  pathInfo: IRegionShort,
} & RouteAndAnalyticsProps & IRegionLayout;

export const DEBUG_TAG = 'region-lodging-page';
export const TEMPLATE_ID = '2.1.5';
export const TEMPLATE_NAME = 'Region Lodging';

export default function RegionLodgingPage({
  accommodations,
  pathInfo,
  featured,
  templateName,
}: RegionLodgingPageProps) {
  useRegionLayoutBubble({});

  const router = useRouter();
  const locale = getLocale();
  const shouldHideWeSki = isRegionWithoutWeSki(locale, pathInfo.uuid);
  const redirectUrl = Routes.regionTripPlanningOverview(pathInfo);

  useEffect(() => {
    if (shouldHideWeSki) {
      router.replace(redirectUrl);
    }
  }, [redirectUrl, router, shouldHideWeSki]);

  if (shouldHideWeSki) {
    return null;
  }

  /**
   * Lodging widget accommodation data such as in/out date + num of guests and rooms
   */
  const [accommodationParams, setAccommodationParams] = useState<ITALodgingReq>({
    checkIn: '',
    checkOut: '',
    numRooms: 1,
    numAdults: 2,
  });

  const hasWeSki = useMemo(() => hasWeSkiIntegration({ regionId: pathInfo.uuid }), [pathInfo.uuid]);
  if (hasWeSki) {
    const destinationId = weSkiDestinationId({ regionId: pathInfo.uuid });
    if (destinationId) {
      return (
        <WeSkiPage
          destinationId={destinationId}
          focusImage={featured?.details?.image}
        />
      );
    }
  }

  const handleChangeAccommodationParams = useCallback(
    (params: ITALodgingReq) => setAccommodationParams(params),
    [setAccommodationParams],
  );

  if (!accommodations) {
    return (
      <Hero400Error light />
    );
  }

  return (
    <div className="w-100">
      <PlanYourTrip
        region={pathInfo}
        hasSkiRental={false}
        hasCarRental={false}
        hasFlights={false}
        onLodgingChange={handleChangeAccommodationParams}
        isPartner={false}
        templateName={templateName}
      />
      <AccommodationsList
        areAccommodationsFresh
        preAccommodations={accommodations}
        searchParams={accommodationParams}
        pathInfo={pathInfo}
      />
    </div>
  );
}

export const fetchProps = async ({ region }: { region: string }): Promise<RegionLodgingPageProps> => {
  const cache: AxiosResponse<IRegionShort> = await assertShouldBeNot404(() => getRegionSlug(region));
  const { uuid } = cache.data;
  const featured = await fetchRegionFeatured(uuid);

  return {
    type: 'region',
    route: 'lodging',
    accommodations: await fetchRegionAccommodations(uuid),
    pathInfo: cache.data,
    numResorts: (await fetchRegionMeta(uuid))?.resorts?.active,
    featured,

    /**
       * Layout
       */
    related: await fetchRegionRelated(uuid),
    navigation: {
      head: HeaderNavCurLink.SNOW_REPORTS,
      tab: CurrentTab.TRIP_PLANNING,
      link: CurrentLink.LODGING,
    },
    adTKey: 'metaregion lodging',
    templateName: TEMPLATE_NAME,
    templateId: TEMPLATE_ID,
    hero: {
      title: 'region.lodging.heroTitle',
    },
    meta: {
      redirectTo: cache.data.domain ?? null,
      description: 'MetaRegion.lodging.description',
      title: 'MetaRegion.lodging.title',
    },
    prefetch: false,
  };
};

// This function gets called at build time
// eslint-disable-next-line max-len
export const getRegionLodgingPageStaticProps = wrapStaticProps(fetchProps);
export const getRegionLodgingPageServerSideProps = wrapServerSideProps(fetchProps);
